import { useState } from "react";
import Label from "../../form/Label";
import Input from "../../form/input/InputField";
import Button from "../button/Button";
import { Modal } from ".";
import { cache, request } from "../../../utils";
import { useNavigate } from "react-router";
import Alert from "../alert/Alert";
import { EyeCloseIcon, EyeIcon } from "../../../icons";

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ResetPasswordModal({
  isOpen,
  onClose: closeModal,
}: ResetPasswordModalProps) {
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(""); // New success state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const email = cache.get("RESET_EMAIL");

  const closeOTPModal = () => {
    closeModal();
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
  };

  // ---- Handle Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    try {
      e.preventDefault();
      setLoading(true);
      setError("");
      setSuccess(""); // Reset success state

      if (password !== confirmPassword) {
        setError("Passwords do not match!");
        setLoading(false);
        return;
      }

      await request.post({
        url: "/user/reset-password",
        data: { email, otp, password },
      });

      setSuccess("Password reset successful. Redirecting...");

      // Wait 3 seconds before navigating
      setTimeout(() => {
        cache.del("RESET_EMAIL");
        closeOTPModal();
        navigate("/", { replace: true });
      }, 3000);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeOTPModal} className="max-w-md p-8">
      <form onSubmit={handleResetPassword}>
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            Reset Password
          </h2>

          {error && (
            <Alert variant="error" title="Error" message={error} showLink={false} />
          )}

          {success && (
            <Alert variant="success" title="Success" message={success} showLink={false} />
          )}

          <div>
            <Label>OTP <span className="text-error-500">*</span></Label>
            <Input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />
          </div>

          <div className="relative">
            <Label>New Password <span className="text-error-500">*</span></Label>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-10 text-gray-500 dark:text-gray-400"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
              ) : (
                <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <Label>Confirm Password <span className="text-error-500">*</span></Label>
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-10 text-gray-500 dark:text-gray-400"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
              ) : (
                <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
              )}
            </button>
          </div>

          <div>
            <Button className="w-full" size="sm" type="submit" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
